import { PageLoader } from '@optools/components/loading/page-loader';
import { Button } from '@optools/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@optools/components/ui/collapsible';
import { useSessionsQuery } from '@optools/store/convivaApiSlice';
import { ChevronUp, Earth, MonitorSmartphone, RadioTower } from 'lucide-react';

import { CreateOverrideDialog } from './create-override-dialog';

export interface ConvivaSessionsProps {
  oktaId: string;
}

export function ConvivaSessions({ oktaId }: ConvivaSessionsProps) {
  const { data, isError, isLoading, refetch } = useSessionsQuery(oktaId);

  return (
    <div className="flex-1">
      <h2 className="mb-2 text-xl font-bold">Conviva Sessions</h2>

      {isLoading || isError ? (
        <div className="relative flex min-h-80 items-center justify-center">
          {isLoading && <PageLoader />}

          {isError && (
            <div className="text-center">
              <p className="mb-4">An error occurred when trying to fetch the sessions.</p>
              <Button onClick={() => refetch()}>Retry</Button>
            </div>
          )}
        </div>
      ) : null}

      {!isError && data?.length === 0 ? (
        <p className="text-sm">No recent Conviva sessions were found.</p>
      ) : null}

      {data && (
        <div className="space-y-2">
          {data?.map(({ conviva_session_id, device_info, geo_info, network_info }) => (
            <Collapsible
              key={conviva_session_id}
              className="w-full overflow-x-auto rounded border bg-muted p-2 pl-3"
              data-testid={`conviva-session-${conviva_session_id}`}>
              <CollapsibleTrigger className="group flex w-full items-center justify-between gap-8">
                <h2 className="truncate text-lg">{conviva_session_id}</h2>
                <ChevronUp className="shrink-0 transition-transform group-data-[state='closed']:rotate-180" />
              </CollapsibleTrigger>

              <CollapsibleContent>
                <div className="mb-2 flex flex-wrap justify-start gap-8 px-2 pt-8">
                  {/* Device */}
                  <div className="basis-48">
                    <div className="mb-2 flex items-center gap-2">
                      <MonitorSmartphone />
                      <h3 className="text-lg font-bold">Device</h3>
                    </div>

                    <dl className="space-y-2">
                      <div className="flex flex-wrap items-center gap-1 text-sm">
                        <dt id="device-name">Name:</dt>
                        <dd aria-labelledby="device-name" className="font-bold">
                          {device_info.name}
                        </dd>
                      </div>

                      <div className="flex flex-wrap items-center gap-1 text-sm">
                        <dt id="device-browser">Browser:</dt>
                        <dd aria-labelledby="device-browser" className="font-bold">
                          {device_info.browser.name}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  {/* Geo */}
                  <div className="basis-72">
                    <div className="mb-2 -ml-0.5 flex items-center gap-2">
                      <Earth />
                      <h3 className="tex-lg font-bold">Geo</h3>
                    </div>

                    <dl className="space-y-2">
                      <div className="flex flex-wrap items-center gap-1 text-sm">
                        <dt id="city">City:</dt>
                        <dd aria-labelledby="city" className="font-bold">
                          {geo_info.city}
                        </dd>
                      </div>

                      <div className="flex flex-wrap items-center gap-1 text-sm">
                        <dt id="state">State:</dt>
                        <dd aria-labelledby="state" className="font-bold">
                          {geo_info.state.name}
                        </dd>
                      </div>

                      <div className="flex flex-wrap items-center gap-1 text-sm">
                        <dt id="country-code">Country Code:</dt>
                        <dd aria-labelledby="country-code" className="font-bold">
                          {geo_info.country.code}
                        </dd>
                      </div>

                      <div className="flex flex-wrap items-center gap-1 text-sm">
                        <dt id="postal-code">Postal Code:</dt>
                        <dd aria-labelledby="postal-code" className="font-bold">
                          {geo_info.postal_code}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  {/* Network */}
                  <div className="flex-1 basis-36">
                    <div className="mb-2 -ml-0.5 flex items-center gap-2">
                      <RadioTower />
                      <h3 className="text-lg font-bold">Network</h3>
                    </div>

                    <dl>
                      <div className="flex flex-wrap items-center gap-1 text-sm">
                        <dt id="ip">IP:</dt>
                        <dd aria-labelledby="ip" className="font-bold">
                          {network_info.ip}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>

                <div className="mt-4 flex justify-end p-2">
                  <CreateOverrideDialog
                    override={{
                      countryCode: geo_info.country.code,
                      ipAddress: network_info.ip,
                      oktaId,
                      zipCode: geo_info.postal_code,
                    }}>
                    {({ onClick }) => <Button onClick={onClick}>Create Override</Button>}
                  </CreateOverrideDialog>
                </div>
              </CollapsibleContent>
            </Collapsible>
          ))}
        </div>
      )}
    </div>
  );
}
